import { useMemo, useState } from 'react';
import {
  EquipmentInspectionSheet,
  InspectionSheetLine,
} from '../../../types/manufacturingEquipment';
import '../../equipment/InspectionSheet.css';
import '../../quality/QualityShell.css';
import '../../product/NonconformanceReportPanel.css';

interface Props {
  sheet: EquipmentInspectionSheet;
}

const MARK_OPTIONS = ['', '/', '△', 'X'] as const;
const H1_MONTHS = ['1월', '2월', '3월', '4월', '5월', '6월'];
const H2_MONTHS = ['7월', '8월', '9월', '10월', '11월', '12월'];

function markKey(lineId: number, halfNo: number, monthIndex: number, weekNo: number) {
  return `${lineId}:${halfNo}:${monthIndex}:${weekNo}`;
}

function dateKey(halfNo: number, monthIndex: number, weekNo: number) {
  return `${halfNo}:${monthIndex}:${weekNo}`;
}

function countMarks(marks: EquipmentInspectionSheet['marks'], lineId: number) {
  const counts = { '/': 0, '△': 0, X: 0, other: 0 };
  (marks ?? []).forEach((m) => {
    if (m.lineId !== lineId || !m.markValue) return;
    if (m.markValue === '/') counts['/'] += 1;
    else if (m.markValue === '△') counts['△'] += 1;
    else if (m.markValue === 'X') counts.X += 1;
    else counts.other += 1;
  });
  return counts;
}

function HalfGrid({
  halfNo,
  months,
  lines,
  markMap,
  dateMap,
}: {
  halfNo: number;
  months: string[];
  lines: InspectionSheetLine[];
  markMap: Record<string, string>;
  dateMap: Record<string, string>;
}) {
  return (
    <div className="insp-sheet__half">
      <table className="insp-sheet__table">
        <thead>
          <tr>
            <th rowSpan={2} className="is-sticky is-sticky-0">
              No
            </th>
            <th rowSpan={2} className="is-sticky is-sticky-1">
              점검개소
            </th>
            <th rowSpan={2} className="is-sticky is-sticky-2">
              점검항목
            </th>
            <th rowSpan={2} className="is-sticky is-sticky-3">
              주기
            </th>
            {months.map((month) => (
              <th key={month} colSpan={5}>
                {month}
              </th>
            ))}
          </tr>
          <tr>
            {months.map((month) =>
              [1, 2, 3, 4, 5].map((week) => (
                <th key={`${month}-${week}`}>{week}</th>
              ))
            )}
          </tr>
        </thead>
        <tbody>
          {lines.length === 0 ? (
            <tr>
              <td colSpan={4 + months.length * 5} className="is-left">
                점검 항목이 없습니다.
              </td>
            </tr>
          ) : (
            lines.map((line, index) => (
              <tr key={line.lineId}>
                <td className="is-sticky is-sticky-0">{index + 1}</td>
                <td className="is-left is-sticky is-sticky-1">{line.inspectionPoint || '-'}</td>
                <td className="is-left is-sticky is-sticky-2">{line.inspectionItem || '-'}</td>
                <td className="is-sticky is-sticky-3">{line.cycleCode || '-'}</td>
                {months.map((_, monthIndex) =>
                  [1, 2, 3, 4, 5].map((weekNo) => {
                    const key = markKey(line.lineId, halfNo, monthIndex, weekNo);
                    return (
                      <td key={key}>
                        <select value={markMap[key] ?? ''} disabled>
                          {MARK_OPTIONS.map((opt) => (
                            <option key={opt || 'empty'} value={opt}>
                              {opt || '·'}
                            </option>
                          ))}
                        </select>
                      </td>
                    );
                  })
                )}
              </tr>
            ))
          )}
          <tr>
            <td colSpan={4} className="is-sticky is-sticky-span">
              점검일자
            </td>
            {months.map((_, monthIndex) =>
              [1, 2, 3, 4, 5].map((weekNo) => {
                const key = dateKey(halfNo, monthIndex, weekNo);
                return (
                  <td key={key}>
                    <input type="date" value={dateMap[key] ?? ''} disabled />
                  </td>
                );
              })
            )}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function InspectionSheetPreviewView({ sheet }: Props) {
  const [halfTab, setHalfTab] = useState<1 | 2>(1);
  const lines = sheet.lines ?? [];
  const actions = sheet.actions ?? [];
  const approvalPending = Boolean(sheet.approvalInProgress);

  const markMap = useMemo(() => {
    const next: Record<string, string> = {};
    (sheet.marks ?? []).forEach((mark) => {
      next[markKey(mark.lineId, mark.halfNo, mark.monthIndex, mark.weekNo)] =
        mark.markValue || '';
    });
    return next;
  }, [sheet.marks]);

  const dateMap = useMemo(() => {
    const next: Record<string, string> = {};
    (sheet.dateCells ?? []).forEach((cell) => {
      next[dateKey(cell.halfNo, cell.monthIndex, cell.weekNo)] = cell.inspectionDate || '';
    });
    return next;
  }, [sheet.dateCells]);

  return (
    <div className="insp-sheet">
      <div className="insp-sheet__meta">
        <span>설비명: {sheet.equipmentName || '-'}</span>
        <span>설비번호: {sheet.equipmentCode || '-'}</span>
        <span>점검년도: {sheet.sheetYear}</span>
        <span>
          상태:{' '}
          {approvalPending
            ? '결재 요청 중'
            : sheet.status === 'CONFIRMED'
              ? '확인완료'
              : '작성중'}
        </span>
        <span>작성: {sheet.createdUserName || '-'}</span>
        <span>최종수정: {sheet.updatedUserName || '-'}</span>
        <span>승인: {sheet.confirmedUserName || '-'}</span>
      </div>

      <div className="insp-sheet__legend">
        범례 — / 양호(급유 불필요) · △ 조정(급유) · X 수리·교체 / 주기 D일 W주 M월 Y년
        <br />
        점검결과 이상(X) 발생 시 하단 조치 내역을 반드시 기록하세요.
      </div>

      <div className="quality-table-wrap" style={{ marginBottom: 12 }}>
        <table className="quality-table">
          <thead>
            <tr>
              <th style={{ width: 48 }}>No</th>
              <th>점검개소</th>
              <th>점검항목</th>
              <th style={{ width: 72 }}>주기</th>
              <th style={{ width: 72 }}>/</th>
              <th style={{ width: 72 }}>△</th>
              <th style={{ width: 72 }}>X</th>
            </tr>
          </thead>
          <tbody>
            {lines.length === 0 ? (
              <tr>
                <td colSpan={7}>점검 항목이 없습니다.</td>
              </tr>
            ) : (
              lines.map((line, index) => {
                const counts = countMarks(sheet.marks, line.lineId);
                return (
                  <tr key={`sum-${line.lineId}`}>
                    <td>{index + 1}</td>
                    <td>{line.inspectionPoint || '-'}</td>
                    <td>{line.inspectionItem || '-'}</td>
                    <td>{line.cycleCode || '-'}</td>
                    <td>{counts['/']}</td>
                    <td>{counts['△']}</td>
                    <td>{counts.X}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="insp-sheet__half-tabs" role="tablist" aria-label="점검 반기">
        <button
          type="button"
          role="tab"
          className={`insp-sheet__half-tab${halfTab === 1 ? ' is-active' : ''}`}
          aria-selected={halfTab === 1}
          onClick={() => setHalfTab(1)}
        >
          상반기 (1~6월)
        </button>
        <button
          type="button"
          role="tab"
          className={`insp-sheet__half-tab${halfTab === 2 ? ' is-active' : ''}`}
          aria-selected={halfTab === 2}
          onClick={() => setHalfTab(2)}
        >
          하반기 (7~12월)
        </button>
      </div>

      <HalfGrid
        halfNo={halfTab}
        months={halfTab === 1 ? H1_MONTHS : H2_MONTHS}
        lines={lines}
        markMap={markMap}
        dateMap={dateMap}
      />

      <div>
        <div className="insp-sheet__section-head">
          <h4>조정·수리 및 교환 후 내용</h4>
        </div>
        <table className="insp-sheet__action-table">
          <thead>
            <tr>
              <th style={{ width: '130px' }}>일자</th>
              <th>이상 내역</th>
              <th>수리내용</th>
              <th>비고</th>
            </tr>
          </thead>
          <tbody>
            {(actions.length
              ? actions
              : [{ actionDate: '', abnormality: '', repairDetail: '', remark: '' }]
            ).map((row, index) => (
              <tr key={`action-${index}`}>
                <td>
                  <input type="date" value={row.actionDate ?? ''} disabled />
                </td>
                <td>
                  <input value={row.abnormality ?? ''} disabled />
                </td>
                <td>
                  <input value={row.repairDetail ?? ''} disabled />
                </td>
                <td>
                  <input value={row.remark ?? ''} disabled />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default InspectionSheetPreviewView;
